/**
 * What the Iconic panel draws, as plain rules (STE-997).
 *
 * Kept apart from the panel so each rule can be checked without a browser:
 * which screen the person is on, the chips their role may use, how chats are
 * grouped by day, when an answer is old, and whether a proposal can still run.
 */

import type { IconicAction, IconicChat, IconicTurn, Role } from '../types'

export type PanelSize = 'side' | 'max' | 'min'

/** How often an answer is checked on, and how many missed checks mean the line is down. */
export const POLL_MS = 3000
/** How often a re-match HR asked for is checked while it runs. */
export const REMATCH_MS = 5000
export const LOST_AFTER = 5
export const LOST = 'Lost contact with Iconic. Your question is safe. Retry.'
export const NOT_CONFIRMED = 'Not done. You never pressed OK. Ask again to do it today.'
export const DELETE_CONFIRM = 'Delete this chat? This does not undo anything Iconic changed.'
/** How long a proposal can be confirmed for, as the server holds it. */
export const FRESH_FOR_MS = 30 * 60 * 1000

/** The screen a path belongs to, in the words the menu uses. */
export function screenOf(pathname: string): string {
  if (pathname.startsWith('/market-data')) return 'Market Data'
  if (pathname.startsWith('/benchmarking')) return 'Benchmarking'
  if (pathname.startsWith('/match-a-job')) return 'Match a job'
  if (pathname.startsWith('/salary-ranges')) return 'Salary Ranges'
  return 'Job Descriptions'
}

/** The record open on the screen, if any: a role, a benchmark or a range, all by role id. */
export function recordOf(pathname: string): string | undefined {
  const m = /^\/(job-descriptions|benchmarking|salary-ranges)\/([A-Za-z0-9_-]+)$/.exec(pathname)
  if (!m || m[2] === 'runs' || m[2] === 'new') return undefined
  return m[2]
}

/**
 * Three suggestions per screen, from a fixed list. Each names only something
 * the person's role can do, so a chip never leads to a refusal.
 */
const HR_CHIPS: Record<string, string[]> = {
  'Job Descriptions': ['Which roles are still drafts?', 'What is waiting for my approval?', 'Show the roles with no benchmark'],
  'Market Data': ['Which surveys do we have?', 'Find Data Engineer in our surveys', 'How many rows does each survey hold?'],
  Benchmarking: ['Which roles found no match?', 'Disagree with this match', 'Benchmark everything that changed'],
  'Match a job': ['Match a job title to our survey', 'What can Match a job do?', 'Save my last paste as a draft role'],
  'Salary Ranges': ['Which ranges are waiting to be accepted?', 'Has anything been published yet?', 'Make the ranges wider'],
}
const MANAGER_CHIPS = ['What changed in this role recently?', 'Ask HR to change this role', 'What have I asked HR for?']

export function chipsFor(role: Role, screen: string, record?: string): string[] {
  if (role === 'manager') return MANAGER_CHIPS
  if (role !== 'admin') return []
  const chips = HR_CHIPS[screen] ?? HR_CHIPS['Job Descriptions']
  // "Disagree with this match" only makes sense with a role open.
  return !record && screen === 'Benchmarking'
    ? chips.map((c) => (c === 'Disagree with this match' ? 'Which roles are out of date?' : c))
    : chips
}

export const placeholderFor = (role: Role) =>
  role === 'manager' ? 'Ask about a role, or ask HR for a change' : 'Ask Iconic, or tell it what to do'

export const footnoteFor = (role: Role) =>
  role === 'manager'
    ? 'Iconic can do only what you can do on Job Descriptions.'
    : 'Iconic only uses your company’s own data. Every change waits for your OK.'

/** Who gets the panel at all: HR admins everywhere, people managers on Job Descriptions, never a super admin. */
export function panelFor(role: Role, screen: string): boolean {
  if (role === 'admin') return true
  if (role === 'manager') return screen === 'Job Descriptions'
  return false
}

/* ---------- days, in the person's own time zone ---------- */

const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime()
const DAY = 24 * 60 * 60 * 1000

export const GROUPS = ['Today', 'Yesterday', 'Previous 7 days', 'Previous 30 days', 'Older'] as const
export type Group = (typeof GROUPS)[number]

export function groupOf(iso: string, now: Date): Group {
  const days = Math.round((startOfDay(now) - startOfDay(new Date(iso))) / DAY)
  if (days <= 0) return 'Today'
  if (days === 1) return 'Yesterday'
  if (days <= 7) return 'Previous 7 days'
  if (days <= 30) return 'Previous 30 days'
  return 'Older'
}

/** Chats under their day headings, newest first, empty headings left out. */
export function groupChats(chats: IconicChat[], now: Date): { group: Group; chats: IconicChat[] }[] {
  const sorted = [...chats].sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))
  return GROUPS
    .map((group) => ({ group, chats: sorted.filter((c) => groupOf(c.updatedAt, now) === group) }))
    .filter((g) => g.chats.length > 0)
}

/** The list without chats deleted in this session, whatever a reload just returned. */
export const withoutDeleted = <T extends { chatId: string }>(chats: T[], deleted: Set<string>): T[] =>
  chats.filter((c) => !deleted.has(c.chatId))

/** An answer given before today, which may no longer match the data. */
export const isOld = (iso: string, now: Date) => startOfDay(new Date(iso)) < startOfDay(now)

export const dayWords = (iso: string) =>
  new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'long' })

export const oldBanner = (iso: string) => `Answered on ${dayWords(iso)}. The figures may have changed since then.`

/** The question an answer belongs to: the turn just before it. */
export function questionFor(turns: IconicTurn[], answer: IconicTurn): string {
  const q = turns.find((t) => t.seq === answer.seq - 1 && t.kind === 'question')
  return q?.text ?? ''
}

/* ---------- action cards ---------- */

/** The button's own verb, so OK says what it does. */
const VERBS: Record<string, string> = {
  proposeDraftRole: 'Add draft',
  proposeRoleChange: 'Change role',
  proposePublishRole: 'Publish',
  proposeDecideChangeRequest: 'Decide',
  proposeChangeRequest: 'Send to HR',
  proposeBenchmarkRun: 'Benchmark',
  proposeStopRun: 'Stop run',
  proposeSurveyJob: 'Save benchmark',
  proposeDisagree: 'Match again',
  proposeKeepMatch: 'Keep',
  proposeSpread: 'Change spread',
  proposePercentile: 'Change percentile',
  proposeAcceptRange: 'Accept',
  proposePublishRanges: 'Publish',
  proposeSaveMatchAsDraft: 'Save as draft',
}
export const verbFor = (kind: string) => VERBS[kind] ?? 'OK'

export type CardShows = 'waiting' | 'running' | 'done' | 'failed' | 'cancelled' | 'notDone'

/**
 * What a proposal's card shows. A waiting one that can no longer run, because
 * it is old or a newer answer came after it, shows as not done with no OK.
 */
export function cardShows(a: IconicAction, onNewestAnswer: boolean, now: number): CardShows {
  if (a.state !== 'waiting') {
    if (a.state === 'failed' && a.result === NOT_CONFIRMED) return 'notDone'
    return a.state
  }
  if (!onNewestAnswer || now - Date.parse(a.proposedAt) >= FRESH_FOR_MS) return 'notDone'
  return 'waiting'
}

/* ---------- what is remembered in this browser ---------- */

export interface Remembered { open: boolean; size: PanelSize; listShown: boolean; chatId: string | null; wasSize: PanelSize }
export const FIRST: Remembered = { open: false, size: 'side', listShown: false, chatId: null, wasSize: 'side' }

const keyFor = (userId: string) => `stello.iconic.${userId}`

export function recall(userId: string): Remembered {
  try {
    const raw = localStorage.getItem(keyFor(userId))
    if (!raw) return FIRST
    const r = JSON.parse(raw) as Partial<Remembered>
    const size = r.size === 'max' || r.size === 'min' ? r.size : 'side'
    const wasSize = r.wasSize === 'max' ? 'max' : 'side'
    return { open: !!r.open, size, listShown: !!r.listShown, chatId: typeof r.chatId === 'string' ? r.chatId : null, wasSize }
  } catch {
    return FIRST
  }
}

export function remember(userId: string, r: Remembered) {
  try { localStorage.setItem(keyFor(userId), JSON.stringify(r)) } catch { /* private window: the panel still works */ }
}

/** Every Iconic call the client makes, so the mock can be held to the same list. */
export const ICONIC_METHODS = [
  'iconicChats', 'startIconicChat', 'iconicChat', 'renameIconicChat', 'deleteIconicChat',
  'askIconic', 'iconicTurn', 'stopIconicTurn', 'confirmIconicAction', 'cancelIconicAction',
] as const

/**
 * Bold and line breaks, nothing else: an answer is text the AI wrote, never
 * markup, so anything else it writes is shown as typed.
 */
export function pieces(text: string): { bold: boolean; text: string }[][] {
  return text.split('\n').map((line) =>
    line.split(/(\*\*[^*]+\*\*)/).filter(Boolean).map((p) =>
      p.startsWith('**') && p.endsWith('**') ? { bold: true, text: p.slice(2, -2) } : { bold: false, text: p }))
}
